import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Plus, Trash2, ChevronUp, ChevronDown, Eye, EyeOff, Copy } from 'lucide-react';

const TYPE_OPTIONS = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'text', label: 'Plain text' },
];

const placeholderFor = (type) => {
  switch (type) {
    case 'email': return 'info@justdive.co.za';
    case 'whatsapp': return '+27 67 535 5405';
    case 'text': return 'Mon–Fri 9am–5pm';
    default: return '+27 67 535 5405';
  }
};

const newItem = () => ({
  id: `contact-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  type: 'phone',
  label: '',
  value: '',
  hidden: false,
});

const ContactUsItemsCard = ({ items, onChange }) => {
  const list = Array.isArray(items) ? items : [];

  const update = (idx, patch) => {
    onChange(list.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const remove = (idx) => {
    if (!window.confirm('Remove this contact entry?')) return;
    onChange(list.filter((_, i) => i !== idx));
  };

  const move = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= list.length) return;
    const next = list.slice();
    const [item] = next.splice(idx, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  const duplicate = (idx) => {
    const src = list[idx];
    const clone = { ...src, id: `contact-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
    const next = list.slice();
    next.splice(idx + 1, 0, clone);
    onChange(next);
  };

  const add = () => onChange([...list, newItem()]);

  return (
    <div className="bg-white rounded-lg border p-6" data-testid="contact-us-card">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="font-semibold text-[#0A2342] mb-1">Contact Us (Footer, right column)</h3>
          <p className="text-xs text-gray-500">
            Add, edit, reorder or hide each entry that appears under "Contact Us" in the footer.
            Choose a type — Phone becomes a call link, Email becomes a mailto link, WhatsApp opens WhatsApp chat.
            Remember to click <span className="font-semibold">Save Settings</span> at the top of the page.
          </p>
        </div>
        <Button
          type="button"
          onClick={add}
          size="sm"
          className="bg-[#0A2342] hover:bg-[#0A2342]/90 text-white shrink-0"
          data-testid="contact-us-add"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Contact
        </Button>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed rounded-lg text-sm text-gray-500">
          No contact entries yet. Click <span className="font-semibold">Add Contact</span> to create one.
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((item, idx) => (
            <div
              key={item.id}
              className={`border rounded-lg p-3 ${item.hidden ? 'bg-gray-50 opacity-70' : 'bg-white'}`}
              data-testid={`contact-us-item-${idx}`}
            >
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="font-semibold text-[#0A2342]">#{idx + 1}</span>
                  {item.hidden && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[11px] font-semibold">
                      HIDDEN
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button type="button" variant="outline" size="sm" onClick={() => move(idx, -1)} disabled={idx === 0} title="Move up" data-testid={`contact-us-up-${idx}`}>
                    <ChevronUp className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => move(idx, 1)} disabled={idx === list.length - 1} title="Move down" data-testid={`contact-us-down-${idx}`}>
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => update(idx, { hidden: !item.hidden })} title={item.hidden ? 'Show on site' : 'Hide from site'} data-testid={`contact-us-hide-${idx}`}>
                    {item.hidden ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => duplicate(idx)} title="Duplicate" data-testid={`contact-us-dup-${idx}`}>
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => remove(idx)} title="Delete" className="text-red-600 hover:bg-red-50" data-testid={`contact-us-delete-${idx}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select
                    className="w-full border rounded-md p-2 text-sm bg-white"
                    value={item.type || 'phone'}
                    onChange={(e) => update(idx, { type: e.target.value })}
                    data-testid={`contact-us-type-${idx}`}
                  >
                    {TYPE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Label <span className="text-gray-400">(optional prefix)</span>
                  </label>
                  <Input
                    value={item.label || ''}
                    onChange={(e) => update(idx, { label: e.target.value })}
                    placeholder="Sales, Bookings…"
                    data-testid={`contact-us-label-${idx}`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Value</label>
                  <Input
                    value={item.value || ''}
                    onChange={(e) => update(idx, { value: e.target.value })}
                    placeholder={placeholderFor(item.type)}
                    data-testid={`contact-us-value-${idx}`}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContactUsItemsCard;
